import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Testimonial {
  id: string;
  name: string;
  location: string | null;
  avatar_url: string | null;
  rating: number;
  text: string;
  product: string | null;
  is_active: boolean;
  position: number;
  created_at: string;
  updated_at: string;
}

export interface TestimonialFormData {
  name: string;
  location?: string;
  avatar_url?: string;
  rating: number;
  text: string;
  product?: string;
  is_active: boolean;
  position: number;
}

export const useTestimonials = () => {
  const queryClient = useQueryClient();

  const { data: testimonials, isLoading } = useQuery({
    queryKey: ["testimonials"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("testimonials")
        .select("*")
        .order("position", { ascending: true });
      if (error) throw error;
      return data as Testimonial[];
    },
  });

  const createTestimonial = useMutation({
    mutationFn: async (formData: TestimonialFormData) => {
      const { error } = await supabase.from("testimonials").insert({
        name: formData.name,
        location: formData.location || null,
        avatar_url: formData.avatar_url || null,
        rating: formData.rating,
        text: formData.text,
        product: formData.product || null,
        is_active: formData.is_active,
        position: formData.position,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["testimonials"] });
      toast.success("Testimonial created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const updateTestimonial = useMutation({
    mutationFn: async ({ id, formData }: { id: string; formData: TestimonialFormData }) => {
      const { error } = await supabase
        .from("testimonials")
        .update({
          name: formData.name,
          location: formData.location || null,
          avatar_url: formData.avatar_url || null,
          rating: formData.rating,
          text: formData.text,
          product: formData.product || null,
          is_active: formData.is_active,
          position: formData.position,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["testimonials"] });
      toast.success("Testimonial updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const deleteTestimonial = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("testimonials").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["testimonials"] });
      toast.success("Testimonial deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  return {
    testimonials,
    isLoading,
    createTestimonial,
    updateTestimonial,
    deleteTestimonial,
  };
};
